import { useEffect, useState } from "react";
import ButtonIconH from "./ButtonIconH";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import Tag from "./Tag";
import { userData, formatValueUnit } from "../js/utils";
import { post } from "../js/post";
import { TabSelector } from "./TabSelector";
import CheckBox from "./CheckBox";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  BarChart,
  Rectangle,
} from "recharts";
import {
  axisStyle,
  axisXLabel,
  axisYLabel,
  barChartMargin,
  barColors,
} from "../js/barChartStyle";
import { useOptics } from "../hooks/useOptics";

export function DashboardPatientStatsPopup({ show, onClose }) {
  const opticalList = useOptics();
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [selectedPanelView, setSelectedPanelView] = useState("Nuevos");
  const [selectedYearView, setSelectedYearView] = useState("5 Años");
  const [data, setData] = useState([]);
  const [selectedOption, setSelectedOption] = useState(0);
  const [toggle, setToggle] = useState({
    vista: true,
    sol: true,
    lc: true,
    audifono: true,
  });

  const customTooltip = ({ payload, label, active }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        className="recharts-default-tooltip"
        style={{
          margin: "0px",
          padding: "10px",
          backgroundColor: "rgb(255, 255, 255)",
          border: "1px solid rgb(204, 204, 204)",
          whiteSpace: "nowrap",
          borderRadius: "4px",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 8px",
        }}
      >
        <p className="recharts-tooltip-label">{`Año: ${label}`}</p>
        <ul
          className="recharts-tooltip-item-list"
          style={{
            padding: "0px",
            margin: "0px",
            listStyleType: "none",
          }}
        >
          {payload.map((item) => (
            <li
              key={item.dataKey}
              className="recharts-tooltip-item"
              style={{ color: item.color }}
            >
              <span className="recharts-tooltip-item-value">
                {`${item.name}: ${formatValueUnit(item.value || 0)}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getViewId = () => {
    const viewMap = {
      "5 Años": 5,
      "10 Años": 10,
    };
    return viewMap[selectedYearView];
  };

  const getBarLegend = (index) => {
    const legends = {
      0: selectedPanelView === "Nuevos" ? "Altas" : "Recurrentes",
      1: "Con compra",
      2: [
        "Con graduación",
        "Con graduación y con compra",
        "Con graduación y sin compra",
      ][index - 1],
      3: [
        "Con graduación",
        "Con graduación y con compra",
        "Con graduación y sin compra",
      ][index - 1],
      4: ["Vista", "Sol", "LC", "Audífonos"][index - 1],
    };

    return legends[selectedOption] || "-";
  };

  const isSimpleBar = () => {
    return selectedOption === 0 || selectedOption === 1;
  };

  const isMultipleBars = () => {
    return selectedOption === 2 || selectedOption === 3;
  };

  const selectableBars = (bar) => {
    if (selectedOption !== 4) return;

    const barMap = {
      1: toggle.vista,
      2: toggle.sol,
      3: toggle.lc,
      4: toggle.audifono,
    };

    return barMap[bar];
  };

  const getData = async () => {
    const response = await post(
      "support-extra/dashboard/ObtenerEstadisticasClientesGraficosDashboard.php",
      {
        token: userData.token,
        tipoCategoria: selectedPanelView === "Nuevos" ? 0 : 1,
        tipoOpcion: selectedOption,
        opticas: opticalList
          .filter((e) => selectedOpticalList.includes(e.text))
          .map((e) => e.id),
        ultimosAnos: getViewId(),
      },
    );
    setData(response);
  };

  const getLegendPayload = () => {
    const items = [];

    if (isSimpleBar() || isMultipleBars() || selectableBars(1)) {
      const label = getBarLegend(1);
      if (label !== "-") {
        items.push({
          value: label,
          type: "rect",
          color: barColors.blue,
        });
      }
    }
    if (isMultipleBars() || selectableBars(2)) {
      const label = getBarLegend(2);
      if (label !== "-") {
        items.push({
          value: label,
          type: "rect",
          color: barColors.orange,
        });
      }
    }
    if (isMultipleBars() || selectableBars(3)) {
      const label = getBarLegend(3);
      if (label !== "-") {
        items.push({
          value: label,
          type: "rect",
          color: barColors.red,
        });
      }
    }
    if (selectableBars(4)) {
      const label = getBarLegend(4);
      if (label !== "-") {
        items.push({
          value: label,
          type: "rect",
          color: barColors.gray,
        });
      }
    }

    return items;
  };

  const CustomLegend = () => {
    const legendItems = getLegendPayload();

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          gap: "20px",
          paddingLeft: "60px",
          paddingTop: "10px",
        }}
      >
        {legendItems.map((item, index) => (
          <div
            key={index}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <div
              style={{
                width: "14px",
                height: "14px",
                backgroundColor: item.color,
                borderRadius: "2px",
              }}
            />
            <span style={{ fontSize: "14px", color: "#666" }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    getData();
  }, [
    selectedPanelView,
    selectedOpticalList,
    selectedOption,
    selectedYearView,
  ]);

  useEffect(() => {
    setSelectedOpticalList([]);
    setSelectedYearView("5 Años");
    setSelectedOption(0);
  }, [onClose]);

  return (
    show && (
      <div className="sales-export-popup">
        <div className="popup-message-extra background-gray">
          <div className="popup-header">
            <div className="popup-title">Estadísticas</div>
            <button
              className="popup-action-icon"
              onClick={() => {
                onClose();
              }}
            >
              <img
                src="images/close-white.svg"
                loading="lazy"
                alt="Close"
                className="popup-close"
              />
            </button>
          </div>
          <div
            className="popup-body"
            style={{
              gap: 0,
              padding: 10,
              margin: 0,
              width: "100%",
            }}
          >
            <div className="container-panel">
              <div className="dashboard-content-panel">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabSelector
                    options={["Nuevos", "Existentes"]}
                    style={{ borderBottom: "none" }}
                    selected={selectedPanelView}
                    onChange={(e) => {
                      setSelectedPanelView(e);
                    }}
                  />
                </div>
              </div>
              <div
                className="thin-section-rounded-border"
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <ButtonIconH
                    text={
                      selectedPanelView === "Nuevos" ? "Altas" : "Recurrentes"
                    }
                    selected={selectedOption === 0}
                    onClick={() => {
                      setSelectedOption(0);
                    }}
                  ></ButtonIconH>
                  <ButtonIconH
                    text="Con compra"
                    selected={selectedOption === 1}
                    onClick={() => {
                      setSelectedOption(1);
                    }}
                  ></ButtonIconH>
                  <ButtonIconH
                    text="Con graduación"
                    selected={selectedOption === 2}
                    onClick={() => {
                      setSelectedOption(2);
                    }}
                  ></ButtonIconH>
                  <ButtonIconH
                    text="Con audiometría"
                    selected={selectedOption === 3}
                    onClick={() => {
                      setSelectedOption(3);
                    }}
                  ></ButtonIconH>
                  <ButtonIconH
                    text="Con ficha"
                    selected={selectedOption === 4}
                    onClick={() => {
                      setSelectedOption(4);
                    }}
                  ></ButtonIconH>
                </div>
                <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                  <div className="data-field">
                    <span className="label">Ópticas</span>
                    <InputIconSearchPanelMultiSelection
                      size={3}
                      type={"type-alphanum-ext"}
                      options={opticalList.map((e) => e.text)}
                      selectedOptions={selectedOpticalList}
                      onChange={(e) => {
                        setSelectedOpticalList(e);
                      }}
                    ></InputIconSearchPanelMultiSelection>
                  </div>
                  {selectedOption === 4 && (
                    <div style={{ display: "flex", gap: 20 }}>
                      <div className="data-field">
                        <span className="label">Vista</span>
                        <CheckBox
                          active={toggle.vista}
                          onClick={() => {
                            setToggle((prev) => ({
                              ...prev,
                              vista: !toggle.vista,
                            }));
                          }}
                        ></CheckBox>
                      </div>
                      <div className="data-field">
                        <span className="label">Sol</span>
                        <CheckBox
                          active={toggle.sol}
                          onClick={() => {
                            setToggle((prev) => ({
                              ...prev,
                              sol: !toggle.sol,
                            }));
                          }}
                        ></CheckBox>
                      </div>
                      <div className="data-field">
                        <span className="label">Lente de contacto</span>
                        <CheckBox
                          active={toggle.lc}
                          onClick={() => {
                            setToggle((prev) => ({
                              ...prev,
                              lc: !toggle.lc,
                            }));
                          }}
                        ></CheckBox>
                      </div>
                      <div className="data-field">
                        <span className="label">Audífonos</span>
                        <CheckBox
                          active={toggle.audifono}
                          onClick={() => {
                            setToggle((prev) => ({
                              ...prev,
                              audifono: !toggle.audifono,
                            }));
                          }}
                        ></CheckBox>
                      </div>
                    </div>
                  )}
                </div>
                {selectedOpticalList.length > 0 && (
                  <span style={{ fontWeight: "500", fontSize: 13 }}>
                    Ópticas seleccionadas: {selectedOpticalList.length}
                  </span>
                )}
                <div className="tag-content">
                  {selectedOpticalList.map((e, i) => {
                    return (
                      <Tag
                        text={e}
                        key={i}
                        tagId={i}
                        onClick={(e) => {
                          setSelectedOpticalList((prev) =>
                            prev.filter((element) => element !== e),
                          );
                        }}
                      ></Tag>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    <TabSelector
                      options={["5 Años", "10 Años"]}
                      selected={selectedYearView}
                      onChange={(e) => {
                        setSelectedYearView(e);
                      }}
                    />
                  </div>
                  <div style={{ width: "100%", height: "550px" }}>
                    <ResponsiveContainer
                      width="100%"
                      height={500}
                      style={{
                        maxWidth: 1000,
                        margin: "0 auto",
                      }}
                    >
                      <BarChart
                        width={500}
                        height={300}
                        data={data}
                        margin={barChartMargin}
                        barCategoryGap={5}
                        barGap={0}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ano"
                          tick={axisStyle}
                          label={axisXLabel("Año")}
                        />
                        <YAxis
                          tick={axisStyle}
                          label={axisYLabel("Número de pacientes")}
                        />
                        <Tooltip content={customTooltip} />
                        <Legend
                          content={<CustomLegend />}
                          verticalAlign="top"
                          align="left"
                          wrapperStyle={{
                            paddingBottom: "0px",
                            marginTop: "-15px",
                          }}
                        />
                        {(isSimpleBar() ||
                          isMultipleBars() ||
                          selectableBars(1)) && (
                          <Bar
                            dataKey="valor1"
                            name={getBarLegend(1)}
                            fill={barColors.blue}
                            activeBar={
                              <Rectangle
                                fill={barColors.blue}
                                stroke={barColors.gray}
                              />
                            }
                          />
                        )}
                        {(isMultipleBars() || selectableBars(2)) && (
                          <Bar
                            dataKey="valor2"
                            name={getBarLegend(2)}
                            fill={barColors.orange}
                            activeBar={
                              <Rectangle
                                fill={barColors.orange}
                                stroke={barColors.gray}
                              />
                            }
                          />
                        )}
                        {(isMultipleBars() || selectableBars(3)) && (
                          <Bar
                            dataKey="valor3"
                            name={getBarLegend(3)}
                            fill={barColors.red}
                            activeBar={
                              <Rectangle
                                fill={barColors.red}
                                stroke={barColors.gray}
                              />
                            }
                          />
                        )}
                        {selectableBars(4) && (
                          <Bar
                            dataKey="valor4"
                            name={getBarLegend(4)}
                            fill={barColors.gray}
                            activeBar={
                              <Rectangle
                                fill={barColors.gray}
                                stroke={barColors.gray}
                              />
                            }
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ButtonIconH
                    text={"Exportar"}
                    small={true}
                    onClick={() => {}}
                  ></ButtonIconH>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
}
